import { useState, useRef, useEffect } from 'react';
import { BRAZILIAN_BANKS } from '../../../mocks/brazilianBanks';

export interface SearchItem {
  code: string;
  name: string;
  fullName: string;
}

interface BankSearchProps {
  value: string;
  bankCode: string;
  onChange: (name: string, code: string) => void;
  placeholder?: string;
  items?: SearchItem[];
  icon?: string;
  codeLabel?: string;
  emptyMessage?: string;
}

export const BankSearch = ({
  value,
  bankCode,
  onChange,
  placeholder = 'Buscar banco ou código...',
  items,
  icon = 'ri-bank-line',
  codeLabel = 'Código',
  emptyMessage = 'Nenhum resultado encontrado',
}: BankSearchProps) => {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const list = items ?? BRAZILIAN_BANKS;

  const filtered = query.trim().length === 0
    ? list.slice(0, 8)
    : list.filter(b =>
        b.name.toLowerCase().includes(query.toLowerCase()) ||
        b.fullName.toLowerCase().includes(query.toLowerCase()) ||
        b.code.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 12);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleSelect = (item: SearchItem) => {
    setQuery(item.name);
    onChange(item.name, item.code === 'n/a' ? '' : item.code);
    setOpen(false);
  };

  const handleManual = () => {
    onChange(query, bankCode);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && filtered[highlighted]) { e.preventDefault(); handleSelect(filtered[highlighted]); }
    if (e.key === 'Escape') setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <i className={`${icon} absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm`} />
        <input
          ref={inputRef}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlighted(0);
            onChange(e.target.value, '');
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
        />
        {query ? (
          <button
            type="button"
            onClick={() => { setQuery(''); onChange('', ''); inputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
        ) : (
          <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm pointer-events-none" />
        )}
      </div>

      {bankCode && (
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <i className="ri-price-tag-3-line text-xs" />
          {codeLabel}: <span className="font-600 text-gray-600">{bankCode}</span>
        </p>
      )}

      {open && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-4 text-center">
              <i className="ri-search-line text-gray-300 text-2xl mb-2 block" />
              <p className="text-sm text-gray-500 mb-2">{emptyMessage}</p>
              {query.trim() && (
                <button
                  type="button"
                  onClick={handleManual}
                  className="text-xs text-emerald-600 font-600 hover:underline cursor-pointer"
                >
                  Usar &quot;{query}&quot; manualmente
                </button>
              )}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto">
              {filtered.map((item, i) => (
                <button
                  key={`${item.code}-${item.name}-${i}`}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                    i === highlighted ? 'bg-emerald-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-gray-500 text-xs`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-600 text-gray-800">{item.name}</p>
                    <p className="text-xs text-gray-400 truncate">{item.fullName}</p>
                  </div>
                  {item.code !== 'n/a' && (
                    <span className="text-xs text-gray-400 font-600 bg-gray-100 px-2 py-0.5 rounded-lg ml-auto flex-shrink-0">
                      {item.code}
                    </span>
                  )}
                </button>
              ))}
              {query.trim() && (
                <button
                  type="button"
                  onClick={handleManual}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50 border-t border-gray-100 cursor-pointer"
                >
                  <i className="ri-add-line text-emerald-500 text-sm" />
                  <span className="text-sm text-emerald-600 font-600">Usar &quot;{query}&quot; manualmente</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
